import { Navigate, useSearchParams } from "react-router-dom";
import { useI18n } from "./i18n";
import { ManagementHeader, type ManagementPageProps } from "./managementUI";
import { useMonitoringData } from "./monitoring/useMonitoringData";
import {
  HealthOverview,
  ComponentMonitoring,
} from "./monitoring/HealthOverview";
import { ExecutionMonitoring } from "./monitoring/ExecutionMonitoring";
import { TeamUsage } from "./monitoring/TeamUsage";
import { date } from "./monitoring/model";
import "./monitoring/monitoring.css";

export function MonitoringPage(props: ManagementPageProps) {
  const [params] = useSearchParams();
  if (params.get("section") === "alerts") {
    const next = new URLSearchParams(params);
    next.delete("section");
    return <Navigate replace to={`/alerts?${next}`} />;
  }
  return <MonitoringContent {...props} />;
}

function MonitoringContent({ refreshKey }: ManagementPageProps) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const { data, loading, now, refresh } = useMonitoringData(refreshKey);
  const pages = [
    ["overview", t("健康总览", "Health overview")],
    ["components", t("组件监控", "Components")],
    ["execution", t("业务执行", "Execution")],
    ["usage", t("团队资源用量", "Team usage")],
  ];
  const alias: Record<string, string> = {
    infrastructure: "components",
    business: "execution",
  };
  const requested = params.get("section") || "overview";
  const section = alias[requested] || requested;
  const active = pages.some(([id]) => id === section) ? section : "overview";
  return (
    <div className="page-stack monitor-page">
      <ManagementHeader
        eyebrow="MONITORING"
        title={t("监控中心", "Monitoring center")}
        description={t(
          "运行健康、执行情况与资源使用。",
          "Runtime health, execution, and resource use.",
        )}
      />
      <div className="monitor-navigation">
        <nav aria-label={t("监控分区", "Monitoring sections")}>
          {pages.map(([id, label]) => (
            <button
              type="button"
              key={id}
              aria-current={active === id ? "page" : undefined}
              className={active === id ? "selected" : ""}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set("section", id);
                setParams(next);
              }}
            >
              {label}
            </button>
          ))}
        </nav>
        <button
          className="secondary-button"
          disabled={loading}
          onClick={() => void refresh()}
        >
          {loading ? t("读取中…", "Loading…") : t("刷新", "Refresh")}
        </button>
      </div>
      <div className="monitor-context">
        <strong>
          {data.telemetry.data?.data_center_id ||
            data.components.data?.data_center_id ||
            "—"}
        </strong>
        <span>
          {t("最近同步", "Last sync")}{" "}
          {date(
            data.components.fetchedAt
              ? new Date(data.components.fetchedAt).toISOString()
              : null,
          )}
        </span>
        <span>{t("每30秒刷新", "Refreshes every 30 seconds")}</span>
      </div>
      {active === "overview" ? <HealthOverview data={data} now={now} /> : null}
      {active === "components" ? (
        <ComponentMonitoring data={data} now={now} />
      ) : null}
      {active === "execution" ? (
        <ExecutionMonitoring data={data} now={now} refreshKey={refreshKey} />
      ) : null}
      {active === "usage" ? <TeamUsage data={data} now={now} /> : null}
    </div>
  );
}
